import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { SkillCategory, SkillSearchParams } from '../../types/skill';

const CATEGORIES: SkillCategory[] = ['process', 'design', 'review', 'generation', 'debugging', 'ops'];

interface SkillSearchBarProps {
  onSearch: (params: Partial<SkillSearchParams>) => void;
  initialParams?: Partial<SkillSearchParams>;
}

export const SkillSearchBar: React.FC<SkillSearchBarProps> = ({ onSearch, initialParams = {} }) => {
  const { darkMode } = useTheme();
  const [q, setQ] = useState(initialParams.q ?? '');
  const [selectedCategory, setSelectedCategory] = useState<SkillCategory | undefined>(initialParams.category);
  const [hideCode, setHideCode] = useState<boolean>(initialParams.hide_code ?? true);

  const emit = useCallback((overrides: Partial<SkillSearchParams> = {}) => {
    onSearch({
      q: q || undefined,
      category: selectedCategory,
      hide_code: hideCode,
      ...overrides,
    });
  }, [q, selectedCategory, hideCode, onSearch]);

  // Use a ref so the debounced callback always reads the latest emit
  const emitRef = useRef(emit);
  emitRef.current = emit;

  const debouncedSearch = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>;
    return (value: string) => {
      clearTimeout(timer);
      timer = setTimeout(() => emitRef.current({ q: value || undefined }), 300);
    };
  }, []);

  const handleQChange = useCallback((value: string) => {
    setQ(value);
    debouncedSearch(value);
  }, [debouncedSearch]);

  const handleCategoryToggle = (cat: SkillCategory) => {
    const next = selectedCategory === cat ? undefined : cat;
    setSelectedCategory(next);
    emit({ category: next });
  };

  const handleHideCodeToggle = () => {
    const next = !hideCode;
    setHideCode(next);
    emit({ hide_code: next });
  };

  return (
    <div className="flex flex-col gap-2">
      <input
        type="text"
        value={q}
        onChange={(e) => handleQChange(e.target.value)}
        placeholder="Search skills..."
        className={`w-full px-3 py-2 rounded-md border text-sm ${
          darkMode
            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
      />
      <div className="flex flex-wrap items-center gap-1.5">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => handleCategoryToggle(cat)}
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
              selectedCategory === cat
                ? 'bg-blue-500 text-white'
                : darkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {cat}
          </button>
        ))}
        {/* Divider */}
        <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>|</span>
        {/* Hide code toggle */}
        <button
          onClick={handleHideCodeToggle}
          title={hideCode ? 'Code skills are hidden — click to show' : 'Click to hide skills with executable code blocks'}
          className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1 ${
            hideCode
              ? darkMode
                ? 'bg-amber-900/50 text-amber-300 border border-amber-700'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
              : darkMode
                ? 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          ⚙ {hideCode ? 'code hidden' : 'show code'}
        </button>
      </div>
    </div>
  );
};
